import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { catchError, finalize, firstValueFrom, forkJoin, of } from 'rxjs';

import type { CentralTransferenciaDto, CorrespondenciaHtmlDto } from '@/core/api/sicom-api/registro/medico/central-transferencia/central-transferencia.model';
import { CentralTransferenciaService } from '@/core/api/sicom-api/registro/medico/central-transferencia/central-transferencia.service';
import { PermissaoService } from '@/core/api/sicom-api/portal/permissao/permissao.service';
import { AuthService } from '@/core/auth/auth.service';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { CardContent } from '@/shared/components/card-content/card-content';
import { DateUtils } from '@/shared/utils/date-utils';
import { MenuItem, PrimeTemplate } from 'primeng/api';
import { ButtonDirective } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { construirMetadataTransferencias } from '../central-transferencia-tabela.config';
import { FotosMedicosService } from '@/shared/services/fotos-medicos.service';
import { IdentificacaoPessoa } from '@/shared/components/pessoa/identificacao-pessoa/identificacao-pessoa';
import { severidadeDataValidade } from '../validade-solicitacao';
import { VigenciaSolicitacao } from '../vigencia-solicitacao/vigencia-solicitacao';
import { CartaoTransferencia } from '../cartao-transferencia/cartao-transferencia';
import { BandeiraUf } from '../bandeira-uf/bandeira-uf';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface FiltroAbertasUfOrigem {
    id: string;
    nomeMedico: string;
    crm: string;
    cpf: string;
    ufDestino: string;
    tipo: string;
}

type ChaveFiltroAbertasUfOrigem = keyof FiltroAbertasUfOrigem;

@Component({
    selector: 'app-abertas-uf-origem',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DialogoPadraoDirective, CommonModule, FormsModule, ButtonDirective, InputText, Select, CardContent, BotaoAcoes, Dialog, CustomTableComponent, IdentificacaoPessoa, VigenciaSolicitacao, BandeiraUf, CartaoTransferencia, PrimeTemplate],
    providers: [FotosMedicosService],
    templateUrl: './abertas-uf-origem.html',
    styleUrl: './abertas-uf-origem.scss'
})
/** Central de transferência: solicitações abertas da UF de origem. */
export class AbertasUfOrigemComponent implements OnInit, OnDestroy {
    customTable = viewChild(CustomTableComponent);

    protected readonly EInconPos = EInconPos;
    private readonly authService = inject(AuthService);
    private readonly centralTransferenciaService = inject(CentralTransferenciaService);
    private readonly fotosMedicos = inject(FotosMedicosService);
    private readonly permissaoService = inject(PermissaoService);
    private readonly sanitizer = inject(DomSanitizer);

    first = 0;
    rows = 10;

    metadataTabela: TableMetadata = construirMetadataTransferencias('UF Destino', 'ufDestinoStr');

    ufOrigem = '';
    nomeUsuario = '';
    carregando = signal(false);
    erroConsulta = '';
    solicitacoes: CentralTransferenciaDto[] = [];
    solicitacoesFiltradas: CentralTransferenciaDto[] = [];
    solicitacaoSelecionada: CentralTransferenciaDto | null = null;
    filtrosVisiveis = false;
    filtrosAplicados: FiltroAbertasUfOrigem = this.criarFiltrosVazios();
    filtrosEdicao: FiltroAbertasUfOrigem = this.criarFiltrosVazios();
    blocosColapsados: Record<ChaveFiltroAbertasUfOrigem, boolean> = {
        id: true,
        nomeMedico: true,
        crm: true,
        cpf: true,
        ufDestino: true,
        tipo: true
    };
    readonly opcoesTipo = [
        { label: 'Todos', value: '' },
        { label: 'Transferência', value: 'T' },
        { label: 'Inscrição Secundária', value: 'S' }
    ];
    dialogCorrespondenciaVisivel = false;
    dialogCarregando = signal(false);
    dialogErro = '';
    dialogTitulo = '';
    correspondenciasHtml: Array<{ titulo: string; html: string }> = [];
    certificadoPdfUrl: SafeResourceUrl | null = null;
    private _rawPdfUrl: string | null = null;
    podeExibirAcoes = signal(false);

    get totalSolicitacoes(): number {
        return this.solicitacoesFiltradas.length;
    }

    get totalTransferencias(): number {
        return this.solicitacoesFiltradas.filter((s) => (s.tipoSolicitacao ?? '').trim().toUpperCase() === 'T' || (s.descricaoTipoSolicitacao ?? '').trim().toUpperCase().includes('TRANSFER')).length;
    }

    get totalSecundarias(): number {
        return this.solicitacoesFiltradas.filter((s) => (s.tipoSolicitacao ?? '').trim().toUpperCase() === 'S' || (s.descricaoTipoSolicitacao ?? '').trim().toUpperCase().includes('SECUND')).length;
    }

    get totalVencidos(): number {
        return this.solicitacoesFiltradas.filter((s) => severidadeDataValidade(s.dataValidade) === 'danger').length;
    }

    get solicitacoesFiltradasPaginadas(): CentralTransferenciaDto[] {
        return this.solicitacoesFiltradas.slice(this.first, this.first + this.rows);
    }

    get solicitacoesFiltradasTabela() {
        return this.solicitacoesFiltradasPaginadas.map((item) => ({
            ...item,
            idSolicitacaoStr: String(item.idSolicitacao ?? '-'),
            // Inscrição do médico: CRM com a UF DELE, no formato do card do quadro. UF ausente
            // faz a linha sumir — melhor que "CRM 4343/" pela metade.
            crmUfChip: item.crm && item.ufOrigem ? `CRM ${item.crm}/${item.ufOrigem.toUpperCase()}` : '',
            medicoPerfil: this.fotosMedicos.perfilDoMedico(item.nomeMedico, item.crm, item.ufOrigem),
            ufDestinoStr: this.formatarUf(item.ufDestino),
            tipoSolicitacaoTag: {
                value: this.obterTipoSolicitacao(item),
                severity: this.obterSeveridadeTipo(item)
            },
            statusStr: this.obterStatus(item),
            dataSolicitacaoStr: this.formatarData(item.dataSolicitacao),
            dataValidadeTag: {
                value: this.formatarData(item.dataValidade),
                severity: severidadeDataValidade(item.dataValidade)
            }
        }));
    }

    onPageChange(event: { first?: number; rows?: number }): void {
        this.first = event.first ?? 0;
        this.rows = event.rows ?? this.rows;
    }

    readonly itensAcoesMenu: MenuItem[] = [
        {
            label: 'Visualizar Documentos',
            icon: 'pi pi-file',
            styleClass: 'bg-blue-500',
            command: () => this.visualizarDocumentos()
        }
    ];

    async ngOnInit(): Promise<void> {
        const user = await this.authService.getUserInfo();
        this.nomeUsuario = user?.name ?? '';
        this.carregarAbertasUfOrigem();
    }

    ngOnDestroy(): void {
        this.revogarUrlPdf();
    }

    carregarAbertasUfOrigem(): void {
        this.erroConsulta = '';
        this.solicitacoes = [];
        this.solicitacoesFiltradas = [];
        this.solicitacaoSelecionada = null;
        this.first = 0;
        this.customTable()?.selectedRow.set(null);
        this.podeExibirAcoes.set(false);

        const ufLogada = this.authService.ufDoUsuario()?.trim().toUpperCase() ?? '';
        this.ufOrigem = ufLogada;

        if (!ufLogada) {
            this.erroConsulta = 'Não foi possível identificar a UF do usuário logado.';
            return;
        }

        void this.atualizarPermissaoAcoes(ufLogada);

        this.carregando.set(true);

        this.centralTransferenciaService
            .listarAbertosOrigem(ufLogada)
            .pipe(finalize(() => this.carregando.set(false)))
            .subscribe({
                next: (response) => {
                    this.solicitacoes = response ?? [];
                    // Retratos entram depois, um por CRM/UF: a foto é enfeite e não pode segurar a
                    // lista. Quando cada uma chega, o mapa do serviço muda e a tabela se redesenha.
                    this.fotosMedicos.carregar(this.solicitacoes.map((solicitacao) => ({ crm: solicitacao.crm, uf: solicitacao.ufOrigem })));
                    this.aplicarListaFiltrada();
                },
                error: (erro) => {
                    this.erroConsulta = this.extrairMensagemErro(erro) ?? 'Não foi possível carregar as solicitações abertas para a UF de origem.';
                }
            });
    }

    formatarData(valor?: string | null): string {
        const data = (valor ?? '').trim();
        if (!data) {
            return '-';
        }

        if (DateUtils.isDateString(data)) {
            return DateUtils.formatDate(data);
        }

        return data;
    }

    obterTipoSolicitacao(item: CentralTransferenciaDto): string {
        return this.valorTexto(item.descricaoTipoSolicitacao) || this.valorTexto(item.tipoSolicitacao) || '-';
    }

    obterStatus(item: CentralTransferenciaDto): string {
        return this.valorTexto(item.descricaoStatus) || this.valorTexto(item.status) || '-';
    }

    obterSeveridadeTipo(item: CentralTransferenciaDto): 'info' | 'success' | 'secondary' {
        const tipoCodigo = this.valorTexto(item.tipoSolicitacao).toUpperCase();
        const tipoDescricao = this.valorTexto(item.descricaoTipoSolicitacao).toUpperCase();

        if (tipoCodigo === 'S' || tipoDescricao.includes('SECUND')) {
            return 'success';
        }

        if (tipoCodigo === 'T' || tipoDescricao.includes('TRANSFER')) {
            return 'info';
        }

        return 'secondary';
    }

    formatarUf(uf?: string | null): string {
        const valor = (uf ?? '').trim().toUpperCase();
        return valor || '-';
    }

    async onSelecaoAlterada(valor: CentralTransferenciaDto | null): Promise<void> {
        this.solicitacaoSelecionada = valor;
        await this.atualizarPermissaoAcoes(this.ufOrigem);
    }

    alternarFiltros(): void {
        this.filtrosVisiveis = !this.filtrosVisiveis;
    }

    alternarColapso(chave: ChaveFiltroAbertasUfOrigem): void {
        this.blocosColapsados[chave] = !this.blocosColapsados[chave];
    }

    iconeColapso(chave: ChaveFiltroAbertasUfOrigem): string {
        return this.blocosColapsados[chave] ? 'pi pi-chevron-down' : 'pi pi-chevron-up';
    }

    aplicarFiltros(): void {
        this.filtrosAplicados = { ...this.filtrosEdicao };
        this.first = 0;
        this.aplicarListaFiltrada();
    }

    restaurarFiltros(): void {
        this.filtrosEdicao = { ...this.filtrosAplicados };
    }

    limparFiltros(): void {
        this.filtrosAplicados = this.criarFiltrosVazios();
        this.filtrosEdicao = this.criarFiltrosVazios();
        this.first = 0;
        this.aplicarListaFiltrada();
    }

    private aplicarListaFiltrada(): void {
        const idFiltro = this.valorTexto(this.filtrosAplicados.id).toLowerCase();
        const nomeFiltro = this.valorTexto(this.filtrosAplicados.nomeMedico).toLowerCase();
        const crmFiltro = this.normalizarNumerico(this.filtrosAplicados.crm);
        const cpfFiltro = this.normalizarNumerico(this.filtrosAplicados.cpf);
        const ufDestinoFiltro = this.valorTexto(this.filtrosAplicados.ufDestino).toUpperCase();
        const tipoFiltro = this.valorTexto(this.filtrosAplicados.tipo).toUpperCase();

        this.solicitacoesFiltradas = this.solicitacoes.filter((item) => {
            const id = this.valorTexto(item.idSolicitacao).toLowerCase();
            const nome = this.valorTexto(item.nomeMedico).toLowerCase();
            const crm = this.normalizarNumerico(item.crm);
            const cpf = this.normalizarNumerico(item.cpf);
            const ufDestino = this.valorTexto(item.ufDestino).toUpperCase();
            const tipo = this.valorTexto(item.tipoSolicitacao).toUpperCase();

            const correspondeId = !idFiltro || id.includes(idFiltro);
            const correspondeNome = !nomeFiltro || nome.includes(nomeFiltro);
            const correspondeCrm = !crmFiltro || crm.includes(crmFiltro);
            const correspondeCpf = !cpfFiltro || cpf.includes(cpfFiltro);
            const correspondeUfDestino = !ufDestinoFiltro || ufDestino.includes(ufDestinoFiltro);
            const correspondeTipo = !tipoFiltro || tipo === tipoFiltro;

            return correspondeId && correspondeNome && correspondeCrm && correspondeCpf && correspondeUfDestino && correspondeTipo;
        });

        if (!this.solicitacaoSelecionada?.idSolicitacao) {
            return;
        }

        const selecionadaAindaExiste = this.solicitacoesFiltradas.some((item) => item.idSolicitacao === this.solicitacaoSelecionada?.idSolicitacao);
        if (!selecionadaAindaExiste) {
            this.solicitacaoSelecionada = null;
            this.customTable()?.selectedRow.set(null);
        }
    }

    visualizarDocumentos(): void {
        this.abrirDialogCorrespondencia('Visualizar Documentos');
    }

    fecharDialogCorrespondencia(): void {
        this.revogarUrlPdf();
        this.certificadoPdfUrl = null;
        this.dialogErro = '';
        this.dialogCorrespondenciaVisivel = false;
        this.dialogCarregando.set(false);
    }

    private abrirDialogCorrespondencia(rotuloAcao: string): void {
        const idSolicitacao = this.solicitacaoSelecionada?.idSolicitacao;
        if (!idSolicitacao) return;

        this.dialogTitulo = `${rotuloAcao} - Solicitação ${idSolicitacao}`;
        this.dialogCorrespondenciaVisivel = true;
        this.dialogCarregando.set(true);
        this.dialogErro = '';
        this.correspondenciasHtml = [];
        this.revogarUrlPdf();
        this.certificadoPdfUrl = null;

        let pdfErro: string | null = null;

        forkJoin({
            html: this.centralTransferenciaService.getCorrespondenciaHtml(idSolicitacao).pipe(catchError(() => of(null))),
            pdf: this.centralTransferenciaService.getCertificadoRegularidadePdf(idSolicitacao, this.nomeUsuario, this.nomeUsuario).pipe(
                catchError((err) => {
                    pdfErro = this.extrairMensagemErro(err) ?? null;
                    return of(null);
                })
            )
        })
            .pipe(finalize(() => this.dialogCarregando.set(false)))
            .subscribe({
                next: ({ html, pdf }) => {
                    const pdfDisponivel = !!pdf;
                    this.correspondenciasHtml = this.mapearCorrespondencias(html, !pdfDisponivel);
                    if (pdf) {
                        this._rawPdfUrl = URL.createObjectURL(pdf);
                        this.certificadoPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this._rawPdfUrl);
                    }
                    if (!this.certificadoPdfUrl && !this.correspondenciasHtml.length) {
                        this.dialogErro = pdfErro ?? 'Nenhuma correspondência disponível para esta solicitação.';
                    }
                },
                error: (erro) => {
                    this.dialogErro = this.extrairMensagemErro(erro) ?? 'Não foi possível carregar as correspondências da solicitação.';
                }
            });
    }

    private mapearCorrespondencias(response?: CorrespondenciaHtmlDto | null, incluirCertificadoHtml = false): Array<{ titulo: string; html: string }> {
        const correspondencias: Array<{ titulo: string; html: string }> = [];

        if (incluirCertificadoHtml) {
            const certificadoRegularidade = this.valorTexto(response?.certificadoRegularidade);
            if (certificadoRegularidade) {
                correspondencias.push({ titulo: 'Certificado de Regularidade', html: certificadoRegularidade });
            }
        }

        const oficioConfirmacao = this.valorTexto(response?.oficioConfirmacao);
        if (oficioConfirmacao) {
            correspondencias.push({ titulo: 'Ofício de Confirmação', html: oficioConfirmacao });
        }

        const oficioRepudio = this.valorTexto(response?.oficioRepudio);
        if (oficioRepudio) {
            correspondencias.push({ titulo: 'Ofício de Repúdio', html: oficioRepudio });
        }

        return correspondencias;
    }

    private valorTexto(valor: unknown): string {
        return (valor ?? '').toString().trim();
    }

    private criarFiltrosVazios(): FiltroAbertasUfOrigem {
        return {
            id: '',
            nomeMedico: '',
            crm: '',
            cpf: '',
            ufDestino: '',
            tipo: ''
        };
    }

    private normalizarNumerico(valor: unknown): string {
        return this.valorTexto(valor).replace(/\D+/g, '');
    }

    private revogarUrlPdf(): void {
        if (this._rawPdfUrl) {
            URL.revokeObjectURL(this._rawPdfUrl);
            this._rawPdfUrl = null;
        }
    }

    private extrairMensagemErro(erro: unknown): string | undefined {
        const e = erro as { error?: { message?: string; detail?: string; error?: string }; message?: string; statusText?: string };
        return e?.error?.message || e?.error?.detail || e?.error?.error || e?.message || e?.statusText;
    }

    private async atualizarPermissaoAcoes(ufBase: string): Promise<void> {
        const ufValidacao = this.normalizarUf(ufBase);
        if (!ufValidacao) {
            this.podeExibirAcoes.set(false);
            return;
        }

        try {
            const resultado = await firstValueFrom(this.permissaoService.validar({ validacoes: { mesmaUf: ufValidacao } }));
            this.podeExibirAcoes.set(!!(resultado?.validacoes?.['mesmaUf'] ?? false));
        } catch {
            this.podeExibirAcoes.set(false);
        }
    }

    private normalizarUf(valor: unknown): string {
        return (valor ?? '')
            .toString()
            .trim()
            .toUpperCase()
            .replace(/[^A-Z]/g, '')
            .slice(0, 2);
    }
}
